/**
 * TypeIconBadge — Tier 0 slice-local PRESENTATIONAL primitive
 * (epic SCRUM-332 / SCRUM-333).
 *
 * A tinted square badge holding a single glyph that connotes a
 * reservation's kind/channel at a glance (list-row leader, detail hero,
 * create-wizard preview). Mirrors `property/_components/TypeIcon` in
 * shape: a decorative `aria-hidden` glyph — the surrounding row/card
 * carries the textual label.
 *
 * **Design source.** `design_handoff_reservations/source/reservations.jsx`
 * `RES_TYPES` / `TypeIconBadge` — VISUAL reference ONLY. The bundle's
 * closed 7-value `RES_TYPES` taxonomy is INVENTED (SCRUM-333: renders win,
 * data model is STALE). The REAL wire has no closed type enum:
 * `stayKind` is a `string` (`PayingGuest|OwnerStay|NonStayBlock`),
 * `reservationTypeKey` is an opaque `string|null`, `bookingChannel` is a
 * `string|null`. `STAY_KIND_WIRE_VALUES` is deliberately NOT published in
 * `@artemis/validators` yet (the closed list lives in the api booking BC,
 * FE-unreachable) — so this primitive stays purely presentational: it
 * keys off a FE-LOCAL glyph union fed by a pure mapper with a SAFE
 * FALLBACK for any unknown string.
 *
 * **Tokens (ADR frontend-0003, SCRUM-78/79).** NO raw brand hex. The
 * bundle tints OTA badges with literal `#FF5A5F` (Airbnb) / `#3D67FF`
 * (VRBO) — forbidden (design-token-guardian BLOCKs hardcoded hex; the
 * inline-numeric + pixel-literal bans are active at 'error'). Instead
 * we tint via SEMANTIC tokens only, using the `OTAChip`
 * `color-mix(in oklab, var(--token) …%, …)` precedent (ADR frontend-0003
 * Tier-2 exception: the recipe resolves at runtime against a canonical
 * token, so we are not branching the token source-of-truth). Geometry
 * (size/radius) uses Tailwind utilities, no pixel literals. True OTA hues,
 * if ever wanted, are a separate `@sparta/design-tokens` ticket.
 *
 * **Tier (ADR frontend-0004).** Tier 0: slice-local, NOT barrel-exported.
 * Promote to `widgets/` on the second-consumer trigger (e.g. the
 * cross-BC dashboard adopting the same glyph mapper).
 */

import * as React from 'react';
import { Icon, type IconName } from '@sparta/ui-kit';

/**
 * FE-LOCAL presentational union. NOT a wire enum — these are glyph
 * BUCKETS the primitive knows how to paint, deliberately decoupled from
 * the (open-string) wire fields so no closed enum is redefined here.
 */
export type TypeGlyphKind =
  | 'ownerBlock' // non-revenue owner-held block
  | 'ownerStay' // owner or guest-of-owner staying
  | 'directGuest' // paying guest booked direct (no channel)
  | 'channelGuest' // paying guest via an OTA/booking channel
  | 'unknown'; // safe fallback for anything out of contract

interface GlyphPresentation {
  /** A registered kit `IconName` (Icon REGISTRY). */
  readonly icon: IconName;
  /** The semantic token the tint + glyph color derive from. */
  readonly token: string;
}

/**
 * glyphKind → (icon, semantic token). Exhaustive over the FE-local union.
 *
 * Glyph choices track the bundle's intent, remapped to the kit REGISTRY
 * (bundle `globe` is NOT a registered IconName → closest semantic is
 * `map` for a channel-sourced guest):
 *   ownerBlock   → `lock`     (bundle owner_block used `lock`)
 *   ownerStay    → `user`     (bundle guest_owner used `user`)
 *   directGuest  → `building` (a PMC/direct booking; bundle pmc used `building`)
 *   channelGuest → `map`      (OTA-sourced; bundle used unregistered `globe`)
 *   unknown      → `receipt`  (a neutral "a reservation of some kind" glyph)
 *
 * Tint tokens are SEMANTIC (never OTA brand hex):
 *   ownerBlock   → --fg-tertiary (muted; non-revenue)
 *   ownerStay    → --info-500
 *   directGuest  → --brand-500
 *   channelGuest → --success-500
 *   unknown      → --fg-tertiary
 */
const GLYPH_PRESENTATION: Readonly<Record<TypeGlyphKind, GlyphPresentation>> = {
  ownerBlock: { icon: 'lock', token: 'var(--fg-tertiary)' },
  ownerStay: { icon: 'user', token: 'var(--info-500)' },
  directGuest: { icon: 'building', token: 'var(--brand-500)' },
  channelGuest: { icon: 'map', token: 'var(--success-500)' },
  unknown: { icon: 'receipt', token: 'var(--fg-tertiary)' },
};

/**
 * Pure mapper: (stayKind, reservationTypeKey?, bookingChannel?) → glyph
 * kind. Every input is treated as an open string (the wire type IS
 * `string`), so every branch is defensive and there is always a
 * `unknown` fallback — an out-of-contract / empty / injection-shaped
 * string can never crash or mis-paint.
 *
 * @param stayKind wire `stayKind` (`PayingGuest|OwnerStay|NonStayBlock` or anything)
 * @param reservationTypeKey opaque wire `reservationTypeKey` (unused for the
 *        glyph today; accepted so callers pass the whole triple and a future
 *        richer mapping needn't change the signature)
 * @param bookingChannel wire `bookingChannel` — presence (a non-empty, non
 *        "direct" string) upgrades a paying guest from `directGuest` to
 *        `channelGuest`
 */
export function glyphKindFor(
  stayKind: string | null | undefined,
  reservationTypeKey?: string | null,
  bookingChannel?: string | null,
): TypeGlyphKind {
  // `reservationTypeKey` is intentionally not branched on yet — the wire
  // value is an opaque catalog key with no FE-known semantics. Referenced
  // so the parameter is not flagged unused; the whole triple is the
  // stable contract for a future richer mapping.
  void reservationTypeKey;

  const kind = typeof stayKind === 'string' ? stayKind.trim() : '';

  if (kind === 'NonStayBlock') return 'ownerBlock';
  if (kind === 'OwnerStay') return 'ownerStay';
  if (kind === 'PayingGuest') {
    return hasChannel(bookingChannel) ? 'channelGuest' : 'directGuest';
  }
  return 'unknown';
}

/** A booking channel is "present" when it is a non-empty string that is
 *  not the sentinel "direct" (case-insensitive). */
function hasChannel(bookingChannel?: string | null): boolean {
  if (typeof bookingChannel !== 'string') return false;
  const c = bookingChannel.trim().toLowerCase();
  return c.length > 0 && c !== 'direct';
}

export interface TypeIconBadgeProps {
  /** Wire `stayKind` (`string`). */
  readonly stayKind: string | null | undefined;
  /** Opaque wire `reservationTypeKey`. */
  readonly reservationTypeKey?: string | null;
  /** Wire `bookingChannel` (`string | null`). */
  readonly bookingChannel?: string | null;
}

export function TypeIconBadge({
  stayKind,
  reservationTypeKey,
  bookingChannel,
}: TypeIconBadgeProps): React.ReactElement {
  const kind = glyphKindFor(stayKind, reservationTypeKey, bookingChannel);
  const { icon, token } = GLYPH_PRESENTATION[kind];

  // `color-mix` recipes off a semantic token (OTAChip / bundle precedent):
  //  - 16% tint over the surface for the background
  //  - 30% over transparent for the hairline border
  // Only token-referencing values live in `style`; no bare numbers, no hex.
  const background = `color-mix(in oklab, ${token} 16%, var(--bg-surface))`;
  const border = `1px solid color-mix(in oklab, ${token} 30%, transparent)`;

  return (
    <span
      aria-hidden="true"
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
      style={{ background, border, color: token }}
    >
      <Icon name={icon} size={14} />
    </span>
  );
}
